import { Link } from "react-router-dom";
import { PageHero } from "../components/Layout";
import { Reveal, SectionHeading } from "../components/Reveal";
import { CollabCTA } from "../components/CollabCTA";
import { useSite } from "../context/SiteContext";
import { mediaUrl } from "../lib/api";
import { useSeo } from "../lib/useSeo";

const ServiceRow = ({ s, i }) => (
  <div className="grid items-center gap-12 md:grid-cols-12 md:gap-10" data-testid={`service-category-${i}`}>
    <Reveal className={`md:col-span-5 ${i % 2 ? "md:order-2 md:col-start-8" : ""}`}>
      <div className="frame relative aspect-[4/5] overflow-hidden">
        <img src={mediaUrl(s.image)} alt={s.title} loading="lazy" className="h-full w-full object-cover" />
      </div>
    </Reveal>
    <Reveal className={`md:col-span-6 ${i % 2 ? "md:order-1" : "md:col-start-7"}`} delay={0.12}>
      <span className="font-serif text-6xl font-light text-champagne">0{i + 1}</span>
      <h2 className="mt-3 font-serif text-4xl font-light text-wine md:text-5xl">{s.title}</h2>
      <p className="mt-6 max-w-md text-base leading-relaxed text-charcoal/85">{s.summary}</p>
      <ul className="mt-10 max-w-md">
        {(s.includes || []).filter((x) => x?.trim()).map((item) => (
          <li key={item} className="flex items-center gap-4 border-t border-line py-4 text-sm text-charcoal md:text-base">
            <span className="h-[5px] w-[5px] rotate-45 bg-wine" />
            {item}
          </li>
        ))}
      </ul>
    </Reveal>
  </div>
);

const PackageCard = ({ p, i }) => (
  <Reveal delay={i * 0.12} className="h-full">
    <div
      data-testid={`package-card-${i}`}
      className={`relative flex h-full flex-col border p-8 md:p-10 ${p.highlighted ? "border-wine bg-white md:-translate-y-6 shadow-[0_30px_60px_-30px_rgba(114,47,55,0.25)]" : "border-line bg-ivory"}`}
    >
      {p.highlighted && <span className="absolute -top-3 left-8 bg-wine px-3 py-1 text-[0.6rem] uppercase tracking-[0.25em] text-ivory">Most Requested</span>}
      <p className="font-serif text-lg italic text-muted-ink">0{i + 1}</p>
      <h3 className="mt-2 font-serif text-3xl text-wine md:text-4xl">{p.name}</h3>
      <p className="mt-8 text-[0.65rem] uppercase tracking-[0.25em] text-muted-ink">Best for</p>
      <p className="mt-2 text-sm leading-relaxed text-charcoal">{p.best_for}</p>
      <p className="mt-6 text-sm leading-relaxed text-charcoal/80">{p.description}</p>
      <div className="mt-auto pt-10">
        <p className="border-t border-line pt-6 font-serif text-2xl italic text-wine" data-testid={`package-price-${i}`}>{p.price_label || "Custom Pricing"}</p>
        <Link to="/contact" className={`btn mt-6 w-full justify-center ${p.highlighted ? "btn-primary" : "btn-secondary"}`} data-testid={`package-cta-${i}`}>
          {p.cta_label || "Request a Quote"}
        </Link>
      </div>
    </div>
  </Reveal>
);

export default function Services() {
  useSeo("Services — Product Photography & Video Ads", "Makeup product photography, jewellery brand content creation and luxury product video ads — single projects, campaigns and ongoing brand collaborations.");
  const { settings } = useSite();
  const services = Array.isArray(settings?.services) ? settings.services : [];
  const packages = Array.isArray(settings?.packages) ? settings.packages : [];
  return (
    <>
      <PageHero
        eyebrow="Services"
        testid="services-title"
        title={<>Thoughtful Creativity for <span className="italic">Beautiful Brands.</span></>}
        text="From a single product launch to ongoing creative collaborations, BWI creates visual content tailored to your brand's needs."
      />
      <section className="mx-auto max-w-[1400px] space-y-28 px-6 pb-28 md:space-y-44 md:px-10 md:pb-44">
        {services.map((s, i) => <ServiceRow key={s.key || i} s={s} i={i} />)}
      </section>
      {packages.length > 0 && (
        <section className="bg-sand" data-testid="packages-section">
          <div className="mx-auto max-w-[1400px] px-6 py-28 md:px-10 md:py-40">
            <SectionHeading eyebrow="Packages" title={<>Ways to <span className="italic">Work Together.</span></>} />
            <Reveal className="mt-6 max-w-lg text-base leading-relaxed text-charcoal/80">
              Every brand is different, so every proposal is shaped around your products, platforms and goals.
            </Reveal>
            <div className="mt-20 grid items-stretch gap-8 md:grid-cols-3">
              {packages.map((p, i) => <PackageCard key={p.name || i} p={p} i={i} />)}
            </div>
          </div>
        </section>
      )}
      <CollabCTA />
    </>
  );
}
